import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useShop } from "../../contexts/ShopContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import {
  getOperatingCosts,
  addOperatingCost,
  deleteOperatingCost,
  updateOperatingCostMain,
} from "../../utils/api/shopApi";
import pageStyles from "../../styles/PageLayout.module.css";
import styles from "./OperatingCostsPage.module.css";

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const sanitizeNumericInput = (value) => {
  const cleaned = String(value ?? "").replace(/[^0-9.]/g, "");
  if (!cleaned) return "";
  const [whole, ...rest] = cleaned.split(".");
  const decimals = rest.join("").slice(0, 2);
  const hasTrailingDot = cleaned.endsWith(".") && rest.length === 1 && !decimals;
  if (hasTrailingDot) return `${whole}.`;
  return decimals.length ? `${whole}.${decimals}` : whole;
};

const normalizeEmptyZero = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (!str || str === "0" || str === "0.0" || str === "0.00") return "";
  return str;
};

const OperatingCostsPage = () => {
  const { t, locale } = useLocalization();
  const isArabic = locale === "ar";
  const { user } = useAuthUser();
  const { shopData } = useShop();
  const authToken = user?.token;
  const userId = user?.userId || user?._id || user?.id || "";
  const isMongoId = (value) => /^[a-f0-9]{24}$/i.test(String(value || ""));
  const shopId = isMongoId(shopData?._id)
    ? shopData._id
    : isMongoId(shopData?.id)
    ? shopData.id
    : null;

  const [loading, setLoading] = useState(true);
  const [operatingCost, setOperatingCost] = useState(null);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [mainCostDraft, setMainCostDraft] = useState({
    annualPackagingFee: "",
    annualPickPackStorageFee: "",
    annualCodFee: "",
  });
  const [estimatedShippingFee, setEstimatedShippingFee] = useState("");
  const [paymentGatewayPercentage, setPaymentGatewayPercentage] = useState("");
  const [paymentGatewayFixedFee, setPaymentGatewayFixedFee] = useState("");
  const [mainCostSaving, setMainCostSaving] = useState(false);

  const loadOperatingCosts = useCallback(async () => {
    if (!userId || !authToken) {
      setOperatingCost(null);
      setEntries([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await getOperatingCosts({ shopId, userId, authToken });
      setOperatingCost(resp.operatingCost);
      setEntries(resp.additionalAnnualOperatingCost ?? []);
      setEstimatedShippingFee(
        normalizeEmptyZero(resp.estimatedShippingFee)
      );
      const paymentFee = resp.estimatedPaymentGatewayFee || {};
      setPaymentGatewayPercentage(
        normalizeEmptyZero(paymentFee.percentage)
      );
      setPaymentGatewayFixedFee(
        normalizeEmptyZero(paymentFee.fixedFee)
      );
    } catch (err) {
      setError(
        err?.message ||
          t("OperatingCostsScreen.loadError") ||
          "Failed to load operating costs."
      );
    } finally {
      setLoading(false);
    }
  }, [authToken, userId, shopId]);

  useEffect(() => {
    loadOperatingCosts();
  }, [loadOperatingCosts]);

  useEffect(() => {
    if (operatingCost) {
      setMainCostDraft({
        annualPackagingFee: normalizeEmptyZero(operatingCost.annualPackagingFee),
        annualPickPackStorageFee: normalizeEmptyZero(
          operatingCost.annualPickPackStorageFee
        ),
        annualCodFee: normalizeEmptyZero(operatingCost.annualCodFee),
      });
    }
  }, [operatingCost]);

  const handleAdd = async () => {
    if (!userId || !authToken) return;
    if (!newLabel.trim()) {
      setError(t("OperatingCostsScreen.labelRequired") || "Label required");
      return;
    }
    setAdding(true);
    setError(null);
    try {
      const payload = { label: newLabel.trim(), amount: Number(newAmount) || 0 };
      const resp = await addOperatingCost({ shopId, userId, payload, authToken });
      setOperatingCost(resp.operatingCost);
      setEntries(resp.additionalAnnualOperatingCost ?? []);
      setNewLabel("");
      setNewAmount("");
    } catch (err) {
      setError(
        err?.message ||
          t("OperatingCostsScreen.addError") ||
          "Failed to add cost."
      );
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!userId || !authToken) return;
    setError(null);
    try {
      const resp = await deleteOperatingCost({
        shopId,
        userId,
        entryId,
        authToken,
      });
      setOperatingCost(resp.operatingCost);
      setEntries(resp.additionalAnnualOperatingCost ?? []);
    } catch (err) {
      setError(
        err?.message ||
          t("OperatingCostsScreen.deleteError") ||
          "Failed to delete cost."
      );
    }
  };

  const handleMainCostChange = (field, value) => {
    const sanitized = sanitizeNumericInput(value);
    setMainCostDraft((prev) => ({ ...prev, [field]: sanitized }));
  };

  const handleSaveMainCosts = async () => {
    if (!userId || !authToken) return;
    setMainCostSaving(true);
    setError(null);
    try {
      const resp = await updateOperatingCostMain({
        shopId,
        userId,
        payload: {
          annualPackagingFee: mainCostDraft.annualPackagingFee || "0",
          annualPickPackStorageFee: mainCostDraft.annualPickPackStorageFee || "0",
          annualCodFee: mainCostDraft.annualCodFee || "0",
          estimatedShippingFee: toNumberOrNull(estimatedShippingFee),
          estimatedPaymentGatewayFee: {
            percentage: toNumberOrNull(paymentGatewayPercentage),
            fixedFee: toNumberOrNull(paymentGatewayFixedFee),
          },
        },
        authToken,
      });
      setOperatingCost(resp.operatingCost);
      setEstimatedShippingFee(
        normalizeEmptyZero(resp.estimatedShippingFee)
      );
      const paymentFee = resp.estimatedPaymentGatewayFee || {};
      setPaymentGatewayPercentage(
        normalizeEmptyZero(paymentFee.percentage)
      );
      setPaymentGatewayFixedFee(
        normalizeEmptyZero(paymentFee.fixedFee)
      );
    } catch (err) {
      setError(
        err?.message ||
          t("OperatingCostsScreen.saveError") ||
          "Failed to save operating costs."
      );
    } finally {
      setMainCostSaving(false);
    }
  };

  const totalAdditional = useMemo(() => {
    return entries.reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  }, [entries]);

  return (
    <section
      className={`${pageStyles.page} ${styles.wrapper} ${isArabic ? styles.rtl : ""}`}
      data-page="operating-costs"
    >
      <header className={pageStyles.sectionHeader}>
        <h2>{t("OperatingCostsScreen.title") || "Operating Costs"}</h2>
        {/* <button className={pageStyles.sectionHeaderButton} onClick={loadOperatingCosts}>
          {t("OperatingCostsScreen.refresh") || "Refresh"}
        </button> */}
      </header>

      <p className={styles.subtitle}>
        {t("OperatingCostsScreen.sub") ||
          "Track annual packaging, pick & pack, COD, and additional fees."}
      </p>

      {error && <p className={styles.error}>{error}</p>}
      {loading && (
        <p className={styles.loading}>
          {t("OperatingCostsScreen.loading") || "Loading operating costs..."}
        </p>
      )}

      {!loading && (
        <div className={styles.grid}>
          <section className={styles.card}>
            <h3>
              {t("OperatingCostsScreen.annualTitle") || "Annual Operating Costs"}
            </h3>
            <div className={styles.fieldRow}>
              <label>
                {t("OperatingCostsScreen.packagingFee") || "Packaging Fee"}
                <input
                  value={mainCostDraft.annualPackagingFee}
                  onChange={(event) =>
                    handleMainCostChange("annualPackagingFee", event.target.value)
                  }
                  inputMode="decimal"
                  pattern="^[0-9]*[.]?[0-9]*$"
                  placeholder="0"
                />
              </label>
              <label>
                {t("OperatingCostsScreen.pickPackStorage") ||
                  "Pick & Pack + Storage"}
                <input
                  value={mainCostDraft.annualPickPackStorageFee}
                  onChange={(event) =>
                    handleMainCostChange(
                      "annualPickPackStorageFee",
                      event.target.value
                    )
                  }
                  inputMode="decimal"
                  pattern="^[0-9]*[.]?[0-9]*$"
                  placeholder="0"
                />
              </label>
              <label>
                {t("OperatingCostsScreen.codFee") || "COD Fee"}
                <input
                  value={mainCostDraft.annualCodFee}
                  onChange={(event) =>
                    handleMainCostChange("annualCodFee", event.target.value)
                  }
                  inputMode="decimal"
                  pattern="^[0-9]*[.]?[0-9]*$"
                  placeholder="0"
                />
              </label>
            </div>

            <div className={styles.fieldRow}>
              <label>
                {t("OperatingCostsScreen.shippingFee") ||
                  "Estimated Shipping Fee"}
                <input
                  value={estimatedShippingFee}
                  onChange={(event) =>
                    setEstimatedShippingFee(
                      sanitizeNumericInput(event.target.value)
                    )
                  }
                  inputMode="decimal"
                  pattern="^[0-9]*[.]?[0-9]*$"
                  placeholder="0"
                />
              </label>
              <label>
                {t("OperatingCostsScreen.gatewayPercent") ||
                  "Payment Gateway %"}
                <input
                  value={paymentGatewayPercentage}
                  onChange={(event) =>
                    setPaymentGatewayPercentage(
                      sanitizeNumericInput(event.target.value)
                    )
                  }
                  inputMode="decimal"
                  pattern="^[0-9]*[.]?[0-9]*$"
                  placeholder="0"
                />
              </label>
              <label>
                {t("OperatingCostsScreen.gatewayFixed") ||
                  "Payment Gateway Fixed Fee"}
                <input
                  value={paymentGatewayFixedFee}
                  onChange={(event) =>
                    setPaymentGatewayFixedFee(
                      sanitizeNumericInput(event.target.value)
                    )
                  }
                  inputMode="decimal"
                  pattern="^[0-9]*[.]?[0-9]*$"
                  placeholder="0"
                />
              </label>
            </div>

            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleSaveMainCosts}
              disabled={mainCostSaving}
            >
              {mainCostSaving
                ? t("OperatingCostsScreen.saving") || "Saving..."
                : t("OperatingCostsScreen.save") || "Save"}
            </button>
          </section>

          <section className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>
                {t("OperatingCostsScreen.additionalTitle") ||
                  "Additional Annual Costs"}
              </h3>
              <span className={styles.total}>
                {(t("OperatingCostsScreen.total") || "Total") + ":"}{" "}
                {totalAdditional.toLocaleString(locale || "en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>

            <div className={styles.addRow}>
              <input
                value={newLabel}
                onChange={(event) => setNewLabel(event.target.value)}
                placeholder={t("OperatingCostsScreen.labelPlaceholder") || "Label"}
              />
              <input
                value={newAmount}
                onChange={(event) =>
                  setNewAmount(sanitizeNumericInput(event.target.value))
                }
                inputMode="decimal"
                pattern="^[0-9]*[.]?[0-9]*$"
                placeholder={t("OperatingCostsScreen.amountPlaceholder") || "Amount"}
              />
              <button type="button" onClick={handleAdd} disabled={adding}>
                {adding
                  ? t("OperatingCostsScreen.adding") || "Adding..."
                  : t("OperatingCostsScreen.add") || "Add"}
              </button>
            </div>

            <div className={styles.list}>
              {entries.map((entry) => (
                <div key={entry._id} className={styles.entryRow}>
                  <span>{entry.label}</span>
                  <span>
                    AED {Number(entry.amount || 0).toLocaleString(locale || "en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={() => handleDelete(entry._id)}
                  >
                    {t("OperatingCostsScreen.remove") || "Remove"}
                  </button>
                </div>
              ))}
              {!entries.length && (
                <p className={styles.empty}>
                  {t("OperatingCostsScreen.emptyAdditional") ||
                    "No additional costs yet."}
                </p>
              )}
            </div>
          </section>
        </div>
      )}
    </section>
  );
};

export default OperatingCostsPage;
